/**
 * UserLayout - Sidebar layout para usuarios normales (company_user / company_admin)
 */

import { Link, useLocation, Outlet } from 'react-router-dom';
import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { NavGuard } from './PermissionGuard';

const Icon = {
  upload: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
      <polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
    </svg>
  ),
  docs: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
      <polyline points="14 2 14 8 20 8"/>
      <line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/>
    </svg>
  ),
  parte: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/>
    </svg>
  ),
  albaran: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
    </svg>
  ),
  admin: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/>
      <rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/>
    </svg>
  ),
  superadmin: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
    </svg>
  ),
  logout: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
      <polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/>
    </svg>
  ),
};

function CompanyBadge({ company }) {
  const branding = company?.branding || {};
  const initials = company ? (company.name || 'E').slice(0, 2).toUpperCase() : 'DF';
  const color = branding.primaryColor || '#273067';
  const name = company?.name || 'DataForge SignOS';
  const [imgError, setImgError] = useState(false);

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '0 4px' }}>
      {branding.logo && !imgError ? (
        <img src={branding.logo} alt={name}
          style={{ width: 36, height: 36, borderRadius: 8, objectFit: 'contain', background: '#fff' }}
          onError={() => setImgError(true)}
        />
      ) : !company ? (
        <img src="/dataforge_blue_64.png" alt="DataForge" style={{ width: 36, height: 36, borderRadius: 8, objectFit: 'contain' }} />
      ) : (
        <div style={{
          width: 36, height: 36, borderRadius: 8, background: color,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontWeight: 700, fontSize: 13, color: '#fff', flexShrink: 0,
          border: '1px solid rgba(255,255,255,0.12)'
        }}>{initials}</div>
      )}
      <div>
        <div style={{ fontWeight: 700, fontSize: 14, color: '#e9eefc', lineHeight: 1.2 }}>{name}</div>
        {!company && <div style={{ fontSize: 9, color: '#9fb0e0', letterSpacing: 2 }}>SIGNOS</div>}
      </div>
    </div>
  );
}

export function UserLayout() {
  const { pathname } = useLocation();
  const { signOut, userData, company, role, claimsLoaded, loading } = useAuth();

  const handleLogout = async () => {
    try { await signOut(); } catch (e) { console.error(e); }
  };

  const isActive = (paths, exact = false) => {
    const list = Array.isArray(paths) ? paths : [paths];
    return list.some(p => exact ? pathname === p : pathname.startsWith(p));
  };

  const displayName = userData?.displayName || userData?.name || userData?.email?.split('@')[0] || 'Usuario';
  const initials = displayName.slice(0, 1).toUpperCase();
  
  // Determinar el texto del rol
  const getRoleText = () => {
    if (!claimsLoaded || loading) return 'Cargando...';
    if (!role) return 'Usuario';
    if (role === 'super_admin') return 'Super Admin';
    if (role === 'company_admin') return 'Admin';
    return 'Usuario';
  };

  return (
    <div style={s.root}>
      <aside style={s.sidebar}>
        {/* Logo empresa */}
        <div style={s.sidebarHeader}>
          <CompanyBadge company={company} />
        </div>

        {/* Nav */}
        <nav style={s.nav}>
          <NavGuard navItem="upload">
            <NavItem to="/upload" icon={Icon.upload} label="Subir"
              active={isActive(['/upload', '/'], true)} />
          </NavGuard>

          <NavGuard navItem="docs">
            <NavItem to="/docs" icon={Icon.docs} label="Documentos"
              active={isActive('/docs')} />
          </NavGuard>

          <NavGuard navItem="solicitudes">
            <NavItem to="/solicitudes" icon={Icon.parte} label="Solicitar Parte"
              active={isActive(['/solicitudes', '/solicitar-parte', '/historial-solicitudes', '/solicitud/'])} />
          </NavGuard>

          <NavGuard navItem="albaranes">
            <NavItem to="/albaranes" icon={Icon.albaran} label="Solicitar Albarán"
              active={isActive(['/albaranes', '/solicitar-albaran', '/historial-albaranes', '/albaran/'])} />
          </NavGuard>

          <NavGuard navItem="deca">
            <NavItem to="/lista-deca" icon="📋" label="Documentos DECA"
              active={isActive(['/lista-deca', '/generar-deca', '/verificar-deca'])} />
          </NavGuard>

          {/* Separador para roles admin */}
          {(role === 'company_admin' || role === 'super_admin') && (
            <div style={s.separator} />
          )}

          <NavGuard navItem="admin">
            <NavItem to="/admin" icon={Icon.admin} label="Panel Admin"
              active={isActive('/admin')} />
          </NavGuard>

          <NavGuard navItem="super-admin">
            <NavItem to="/super-admin" icon={Icon.superadmin} label="Super Admin"
              active={isActive('/super-admin')} />
          </NavGuard>
        </nav>

        {/* Footer usuario */}
        <div style={s.sidebarFooter}>
          <Link to="/profile" style={{ ...s.userRow, textDecoration: 'none' }}>
            <div style={s.avatar}>{initials}</div>
            <div style={{ minWidth: 0 }}>
              <div style={s.userName}>{displayName}</div>
              <div style={s.userRole}>
                {getRoleText()}
              </div>
            </div>
          </Link>
          <button onClick={handleLogout} style={s.logoutBtn} title="Cerrar sesión">
            {Icon.logout}
          </button>
        </div>
      </aside>

      <main style={s.main}>
        <div className="light-content">
          <Outlet />
        </div>
      </main>

      <style>{`
        .light-content {
          --bg: #ffffff; --card: #ffffff; --text: #0f172a;
          --muted: #64748b; --primary: #eef2ff; --primary-strong: #4f46e5;
          color: #0f172a; background: #f1f5f9;
        }
        /* Fondos oscuros → blanco */
        .light-content [style*="#0b1028"], .light-content [style*="#0d1330"],
        .light-content [style*="#0d1430"], .light-content [style*="#0f1536"],
        .light-content [style*="#080e24"], .light-content [style*="#0a0f2e"],
        .light-content [style*="#111827"], .light-content [style*="#1a1a2e"] {
          background: #ffffff !important; color: #0f172a !important; border-color: #e2e8f0 !important;
        }
        .light-content .card {
          background: #ffffff !important; border: 1px solid #e2e8f0 !important;
          box-shadow: 0 1px 3px rgba(0,0,0,0.06) !important; color: #0f172a !important;
        }
        .light-content .title,
        .light-content h1, .light-content h2, .light-content h3, .light-content h4 { color: #0f172a !important; font-weight: 600; }
        .light-content .kicker, .light-content .meta, .light-content label { color: #64748b !important; }
        .light-content p { color: #475569 !important; }
        .light-content .btn { background: #1e293b !important; color: #fff !important; border: none !important; }
        .light-content .btn:hover { background: #0f172a !important; }
        .light-content .copy { background: #fff !important; border: 1px solid #e2e8f0 !important; color: #374151 !important; }
        .light-content .copy:hover { background: #f8fafc !important; }
        .light-content input, .light-content textarea, .light-content select {
          background: #fff !important; color: #0f172a !important; border: 1px solid #e2e8f0 !important;
        }
        .light-content input::placeholder, .light-content textarea::placeholder { color: #94a3b8 !important; }
        .light-content input:focus, .light-content textarea:focus, .light-content select:focus {
          border-color: #1e293b !important; box-shadow: 0 0 0 3px rgba(30,41,59,0.08) !important; outline: none !important;
        }
        .light-content table { color: #0f172a !important; }
        .light-content th { color: #374151 !important; background: #f8fafc !important; border-bottom: 2px solid #e2e8f0 !important; font-weight: 600; }
        .light-content td { border-bottom: 1px solid #f1f5f9 !important; color: #0f172a !important; }
        .light-content tr:hover td { background: #f8fafc !important; }
        .light-content .drop { background: #f8fafc !important; border: 2px dashed #cbd5e1 !important; color: #64748b !important; }

        /* ── Badges de estado con colores ── */
        .light-content .badge { font-weight: 500 !important; font-size: 12px !important; border-radius: 6px !important; padding: 3px 10px !important; }
        /* Verde: firmado, completado */
        .light-content [style*="background: #10b981"], .light-content [style*="background:#10b981"],
        .light-content [style*="background: #22c55e"], .light-content [style*="background:#22c55e"],
        .light-content [style*="background: green"] {
          background: #dcfce7 !important; color: #166534 !important; border: 1px solid #bbf7d0 !important;
        }
        /* Azul: pendiente, parte de trabajo */
        .light-content [style*="background: #3b82f6"], .light-content [style*="background:#3b82f6"],
        .light-content [style*="background: #60a5fa"], .light-content [style*="background:#60a5fa"],
        .light-content [style*="background: blue"] {
          background: #dbeafe !important; color: #1e40af !important; border: 1px solid #bfdbfe !important;
        }
        /* Naranja/amarillo: en proceso, enviado */
        .light-content [style*="background: #f59e0b"], .light-content [style*="background:#f59e0b"],
        .light-content [style*="background: #fb923c"], .light-content [style*="background:#fb923c"],
        .light-content [style*="background: orange"] {
          background: #fef3c7 !important; color: #92400e !important; border: 1px solid #fde68a !important;
        }
        /* Rojo: rechazado, error */
        .light-content [style*="background: #ef4444"], .light-content [style*="background:#ef4444"],
        .light-content [style*="background: red"] {
          background: #fee2e2 !important; color: #991b1b !important; border: 1px solid #fecaca !important;
        }
        /* Morado: albarán */
        .light-content [style*="background: #8b5cf6"], .light-content [style*="background:#8b5cf6"],
        .light-content [style*="background: #a78bfa"], .light-content [style*="background:#a78bfa"] {
          background: #ede9fe !important; color: #5b21b6 !important; border: 1px solid #ddd6fe !important;
        }
        /* Colores de texto naranja (IDs de documento) — mantener */
        .light-content [style*="color: #f59e0b"], .light-content [style*="color:#f59e0b"],
        .light-content [style*="color: orange"], .light-content [style*="color: #fb923c"],
        .light-content [style*="color:#fb923c"] { color: #c2410c !important; font-weight: 600 !important; }
      `}</style>
    </div>
  );
}

function NavItem({ to, icon, label, active }) {
  return (
    <Link
      to={to}
      style={{
        ...s.navItem,
        background: active ? 'rgba(79,108,247,0.18)' : 'transparent',
        color: active ? '#c5d0f5' : '#6b80b8',
        borderLeft: active ? '3px solid #4f6cf7' : '3px solid transparent',
        paddingLeft: active ? 13 : 16,
      }}
      onMouseEnter={(e) => {
        if (!active) {
          e.currentTarget.style.background = 'rgba(79,108,247,0.08)';
          e.currentTarget.style.color = '#a0b0e0';
        }
      }}
      onMouseLeave={(e) => {
        if (!active) {
          e.currentTarget.style.background = 'transparent';
          e.currentTarget.style.color = '#6b80b8';
        }
      }}
    >
      <span style={{ flexShrink: 0 }}>{icon}</span>
      <span>{label}</span>
    </Link>
  );
}

export default UserLayout;

const SIDEBAR_W = 240;

const s = {
  root: {
    display: 'flex',
    minHeight: '100vh',
    background: '#0b1028',
  },
  sidebar: {
    width: SIDEBAR_W,
    flexShrink: 0,
    background: '#080e24',
    borderRight: '1px solid #151f45',
    display: 'flex',
    flexDirection: 'column',
    position: 'fixed',
    top: 0, left: 0,
    height: '100vh',
    zIndex: 50,
    overflowY: 'auto',
  },
  sidebarHeader: {
    padding: '22px 16px 18px',
    borderBottom: '1px solid #151f45',
    flexShrink: 0,
  },
  nav: {
    flex: 1,
    padding: '16px 10px',
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  navItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '10px 14px 10px 16px',
    borderRadius: 8,
    textDecoration: 'none',
    fontSize: 13,
    fontWeight: 500,
    transition: 'all 0.15s',
  },
  separator: {
    height: 1,
    background: '#151f45',
    margin: '10px 6px',
  },
  sidebarFooter: {
    padding: '14px 16px',
    borderTop: '1px solid #151f45',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexShrink: 0,
  },
  userRow: {
    display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0,
    cursor: 'pointer',
    transition: 'background 0.15s',
    borderRadius: 8,
    padding: '6px',
    margin: '-6px',
  },
  avatar: {
    width: 32, height: 32, borderRadius: '50%',
    background: '#1e2d6b',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontWeight: 700, fontSize: 13, color: '#c5d0f5', flexShrink: 0,
  },
  userName: {
    fontSize: 12, fontWeight: 600, color: '#dde4f8',
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  },
  userRole: { fontSize: 10, color: '#6b80b8', marginTop: 1 },
  logoutBtn: {
    background: 'transparent', border: 'none', color: '#6b80b8',
    cursor: 'pointer', padding: 6, borderRadius: 6,
    display: 'flex', alignItems: 'center', flexShrink: 0,
    transition: 'color 0.15s',
  },
  main: {
    flex: 1,
    marginLeft: SIDEBAR_W,
    minWidth: 0,
    minHeight: '100vh',
    padding: '28px 32px',
    boxSizing: 'border-box',
    background: '#f1f5f9',
    color: '#0f172a',
    // Sobreescribir variables CSS para tema claro
    '--bg': '#f1f5f9',
    '--card': '#ffffff',
    '--text': '#0f172a',
    '--muted': '#64748b',
    '--primary': '#e2e8f0',
    '--primary-strong': '#1e293b',
  },
};
